import React from 'react';
import { Modal, Button, HorizontalTabBar, colors, Tooltip } from '@insurancemenu/imenu-components-lib';
import clsx from 'clsx';
import { createStyles as createModalStyles } from 'components/common/modals/ActionModal/styles';
import { observer } from 'mobx-react-lite';
import { Add, Delete } from '@insurancemenu/imenu-components-lib/dist/icons';
import DynamicFormFromJSON from 'components/common/form/DynamicFormFromJSON';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import ManageDependentsStore from 'store/ui-stores/elections/manageDependentsStore';
import { Subscriber } from 'types/electionService';
import { createStyles } from './styles';

export interface ManageDependentsModalProps {
  modalStore: ManageDependentsStore;
  subscriber: Subscriber;
  /**
   * Close modal method with page update
   */
  onClose: () => void;
}

const ManageDependentsModal = observer(
  ({ modalStore, subscriber, onClose }: ManageDependentsModalProps): JSX.Element => {
    const classes = createStyles();
    const modalStyles = createModalStyles(true); // common modal styles

    const {
      loading,
      isManageDependentsModalOpen,
      activeDependentId,
      dynamicForm,
      form,
      addDependent,
      updateDependent,
      setActiveDependentId,
      removeDependent,
      showTabToAddNewDependent,
    } = modalStore;

    const dependentsList = subscriber.custom.dependents;
    const newDependentFormIsOpen = activeDependentId === '';

    const tabs = dependentsList.map((dependent) => {
      return {
        id: dependent.id,
        label: `${dependent?.firstName} ${dependent?.lastName}`,
      };
    });

    const tabsWithNewDependent = [
      ...tabs,
      {
        id: '',
        label: 'New Dependent',
        className: classes.highlightedTabItem,
      },
    ];

    const AddDependentButton = (
      <Button
        color="custom"
        customColor={colors.green[600]}
        leftIconProp={<Add />}
        size="medium"
        className={classes.addDependentsButton}
        onClick={showTabToAddNewDependent}
        disabled={newDependentFormIsOpen}
      >
        Add Dependent
      </Button>
    );

    const onSubmit = async (): Promise<void> => {
      if (activeDependentId) {
        await updateDependent(activeDependentId);
      }
      if (activeDependentId === '') {
        await addDependent();
      }
      onClose();
    };

    const onRemoveDependent = (): void => {
      if (activeDependentId) {
        removeDependent(activeDependentId);
        onClose();
      }
    };

    return (
      <Modal
        show={isManageDependentsModalOpen}
        onClose={onClose}
        className={clsx(modalStyles.modal, classes.manageDependentModal)}
      >
        <>
          <h3 className={modalStyles.modalTitle}>Manage Dependents</h3>
          <p className={classes.disclaimer}>
            <b>IMPORTANT!</b> If you add a spouse or dependent here, InsuranceMenu will not automatically elect
            coverage. Please check each product line and add a check for each newly added spouse or dependent signifying
            participation.
          </p>
          {/*
           Prevent creating new dependents until the current form is completed.
           Limit to one dependent at a time. Allow adding if a new dependent form is open.
        */}
          {newDependentFormIsOpen ? (
            <Tooltip label="Complete current form to add another dependent." alignment={AlignmentTypes.BOTTOM_CENTER}>
              {AddDependentButton}
            </Tooltip>
          ) : (
            AddDependentButton
          )}
          {dependentsList.length > 0 || newDependentFormIsOpen ? (
            <HorizontalTabBar
              tabs={activeDependentId ? tabs : tabsWithNewDependent}
              activeTabId={activeDependentId || ''}
              setActiveTab={setActiveDependentId}
              className={classes.tabs}
            >
              <div className={classes.dependentSectionContent}>
                {/* Show remove button only for already added dependents */}
                {activeDependentId && (
                  <Button
                    type="text"
                    color="custom"
                    customColor={colors.red[600]}
                    leftIconProp={<Delete />}
                    className={classes.removeDependentButton}
                    onClick={onRemoveDependent}
                    disabled={loading}
                  >
                    Remove Dependent
                  </Button>
                )}
                <DynamicFormFromJSON
                  dynamicForm={dynamicForm}
                  form={form}
                  loading={loading}
                  formBodyClassName={classes.formBodyClassName}
                  onCompleteForm={dependentsList.length > 0 || newDependentFormIsOpen ? onSubmit : undefined}
                  completeBtnText={activeDependentId ? 'Update' : 'Add Dependent'}
                  onBack={onClose}
                  backBtnText="Close"
                />
              </div>
            </HorizontalTabBar>
          ) : (
            <div>
              <p className={clsx(modalStyles.contentText, classes.noDependentsMessage)}>
                Currently no Dependents added. Begin by adding the first one.
              </p>
              <footer className={classes.modalFooter}>
                <Button type="outlined" onClick={onClose} size="large" className={classes.closeButton}>
                  Close
                </Button>
              </footer>
            </div>
          )}
        </>
      </Modal>
    );
  },
);

export default ManageDependentsModal;
